import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { elementPerClau } from '@/navigation/navigation';
import { SensePermis } from '@/pages/EstatsPagina';
import { useMe } from '@/permisos/MeContext';
import { compleix } from '@/permisos/permisos';
import { GRUPS_AJUSTOS } from './marc/grups';
import './administracio.css';

/** Grups de l'índex d'Ajustos: els mateixos que el menú lateral. */
const GRUPS = GRUPS_AJUSTOS;

/** Normalitza per cercar sense accents ni majúscules. */
function normalitza(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Cada paraula de la cerca ha d'aparèixer al text; les llargues es comparen sense l'última
 * lletra perquè «plantilla» trobi «Plantilles» i «importar» trobi «importació».
 */
function coincideix(text: string, q: string): boolean {
  const t = normalitza(text);
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => t.includes(w.length > 4 ? w.slice(0, -1) : w));
}
/**
 * Índex d'Ajustos: tot el que es configura un cop i es toca poc, fora del menú del dia a dia.
 * Cada enllaç només es mostra si l'usuari té permís sobre la pantalla.
 */
export function Ajustos() {
  const { t } = useTranslation('administracio');
  const { me } = useMe();
  const [cerca, setCerca] = useState('');
  const q = normalitza(cerca.trim());

  const grups = GRUPS.map((g) => ({
    ...g,
    items: g.items
      .map((clau) => ({ clau, element: elementPerClau(clau) }))
      .filter(({ element }) => element && compleix(me, element.permis))
      .map(({ clau, element }) => ({
        clau,
        ruta: element?.ruta ?? '/admin',
        icona: element?.icona ?? 'gear',
        titol: t(`ajustos.items.${clau}.titol`),
        text: t(`ajustos.items.${clau}.text`),
      })),
  })).filter((g) => g.items.length > 0);

  if (grups.length === 0) return <SensePermis />;

  const filtrats = grups
    .map((g) => ({
      ...g,
      items: q ? g.items.filter((i) => coincideix(`${i.titol} ${i.text}`, q)) : g.items,
    }))
    .filter((g) => g.items.length > 0);

  return (
    <section className="pantalla" data-screen="AJ-00">
      <Toolbar titol={t('ajustos.titol')} subtitol={t('ajustos.subtitol')} />
      <div className="content stack ajustos">
        <label className="search ajustos__cerca">
          <Icona nom="magnifier" />
          <input
            type="search"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={t('ajustos.cerca')}
            aria-label={t('ajustos.cercaEtiqueta')}
          />
        </label>
        {filtrats.length === 0 ? (
          <p className="dim" role="status">
            {t('ajustos.senseResultats')}
          </p>
        ) : (
          <div className="ajustos__grups">
            {filtrats.map((g) => (
              <section
                key={g.clau}
                className="panel ajustos__grup"
                aria-labelledby={`ajustos-${g.clau}`}
              >
                <h2 id={`ajustos-${g.clau}`}>{t(`marc.grups.${g.clau}`)}</h2>
                {g.items.map((i) => (
                  <Link key={i.clau} className="ajustos__item" to={i.ruta} data-ajust={i.clau}>
                    <span className="ajustos__icona">
                      <Icona nom={i.icona} />
                    </span>
                    <span className="ajustos__text">
                      <strong>{i.titol}</strong>
                      <span>{i.text}</span>
                    </span>
                    <Icona nom="chevron-right" className="ajustos__chev" />
                  </Link>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
